import OktaAuth from '@okta/okta-auth-js';

// Okta Configuration
// Eutelsat Okta requires Authorization Code + PKCE (implicit flow is disabled)
const oktaConfig = {
  issuer: 'https://auth.eutelsat.net/oauth2/default',
  clientId: '0oatq76odb2AY3C49417',
  redirectUri: window.location.origin + '/',
  scopes: ['openid', 'profile', 'email'],
  pkce: true,
  responseType: 'code' // Authorization code flow (required when implicit is disabled)
};

// Initialize Okta Auth instance
export const oktaAuth = new OktaAuth(oktaConfig);

const ISSUER = oktaConfig.issuer;
const WELL_KNOWN_URL = ISSUER + '/.well-known/openid-configuration';

// Pre-populate well-known cache so SDK skips the fetch (Eutelsat may block/restrict it).
// This avoids "You do not have permission to access the feature" when using PKCE.
function seedWellKnownCache() {
  try {
    const httpCache = oktaAuth.storageManager.getHttpCache(oktaAuth.options.cookies);
    const cache = httpCache.getStorage() || {};
    if (!cache[WELL_KNOWN_URL]) {
      cache[WELL_KNOWN_URL] = {
        response: {
          issuer: ISSUER,
          authorization_endpoint: ISSUER + '/v1/authorize',
          token_endpoint: ISSUER + '/v1/token',
          jwks_uri: ISSUER + '/v1/keys',
          userinfo_endpoint: ISSUER + '/v1/userinfo',
          code_challenge_methods_supported: ['S256', 'plain'],
          response_types_supported: ['code', 'token', 'id_token'],
          scopes_supported: ['openid', 'profile', 'email'],
        },
        expiresAt: Math.floor(Date.now() / 1000) + 86400, // 24h
      };
      httpCache.setStorage(cache);
    }
  } catch (e) {
    console.warn('seedWellKnownCache:', e);
  }
}

// Initialize function (for compatibility with existing code)
export const msalAsync = async () => {
  // Okta Auth initializes automatically, but we can check if user is already authenticated
  const isAuthenticated = await oktaAuth.isAuthenticated();
  if (isAuthenticated) {
    const idToken = await oktaAuth.tokenManager.get('idToken');
    if (idToken) {
      // User is already authenticated, restore session
      // Decode token to get user info
      const claims = idToken.claims;
      localStorage.setItem(
        "auth",
        JSON.stringify({
          access: idToken.value,
          refresh: idToken.value,
          user: claims.email || claims.sub || claims.preferred_username,
        })
      );
    }
  }
}

// Export for backward compatibility
export const msalInstance = oktaAuth;

// Logout function
// export const logout = async () => {
//   try {
//     // Clear Okta session
//     await oktaAuth.signOut();
    
//     // Clear local storage
//     localStorage.removeItem("idToken");
//     localStorage.removeItem("msalLoginData");
//     localStorage.removeItem("auth");
    
//     // Redirect to home
//     window.location.href = '/';
//   } catch (error) {
//     console.error('Logout error:', error);
//     // Clear local storage even if Okta logout fails
//     localStorage.removeItem("idToken");
//     localStorage.removeItem("msalLoginData");
//     localStorage.removeItem("auth");
//     window.location.href = '/';
//   }
// }

// Login function
export const login = async (navigate) => {
  try {

    // Temporary redirect to dashboard for testing
    navigate("/dashboard");
    return;

    // Check if user is already authenticated
    const isAuthenticated = await oktaAuth.isAuthenticated();
    
    if (isAuthenticated) {
      // User is already logged in, get tokens
      const idToken = await oktaAuth.tokenManager.get('idToken');
      
      if (idToken) {
        // Get user info from token claims
        const claims = idToken.claims;
        
        // Store tokens and user info
        localStorage.setItem("idToken", idToken.value);
        localStorage.setItem("msalLoginData", JSON.stringify({
          account: {
            username: claims.email || claims.sub || claims.preferred_username,
            homeAccountId: claims.sub
          }
        }));
        localStorage.setItem(
          "auth",
          JSON.stringify({
            access: idToken.value,
            refresh: idToken.value,
            user: claims.email || claims.sub || claims.preferred_username,
          })
        );
        
        navigate("/dashboard");
        return;
      }
    }
    
    // User is not authenticated, start login flow (Authorization Code + PKCE)
    // Seed well-known cache so SDK doesn't fetch discovery (avoids permission error)
    seedWellKnownCache();
    oktaAuth.token.getWithRedirect({
      responseType: 'code',
      scopes: ['openid', 'profile', 'email']
    });
    
  } catch (error) {
    console.error('Login error:', error);
  }
}

// Handle callback after redirect (call this in your App.js or main component)
export const handleOktaCallback = async () => {
  try {
    // Parse tokens from the callback URL
    const tokens = await oktaAuth.token.parseFromUrl();
    
    // Store tokens
    oktaAuth.tokenManager.setTokens(tokens.tokens);
    
    // Get user info from token claims
    const idToken = tokens.tokens.idToken;
    const claims = idToken.claims;
    
    // Store in localStorage
    localStorage.setItem("idToken", idToken.value);
    localStorage.setItem("msalLoginData", JSON.stringify({
      account: {
        username: claims.email || claims.sub || claims.preferred_username,
        homeAccountId: claims.sub
      }
    }));
    localStorage.setItem(
      "auth",
      JSON.stringify({
        access: idToken.value,
        refresh: idToken.value,
        user: claims.email || claims.sub || claims.preferred_username,
      })
    );
    
    return true;
  } catch (error) {
    console.error('Callback error:', error);
    return false;
  }
}